import { useState, Fragment } from "react";
import { FileText, PackageCheck, Printer, Layers } from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import { money } from "../lib/api";
import { printReceipt, printIndent } from "../lib/printing";

const CONFIGS = {
  inventory: {
    key: "inventory",
    title: "Item catalogue",
    sub: "Packaging rules, rates, batches (FIFO), and live stock",
    cols: ["Item", "Category", "Supply vendor", "Stock / Batches", "Rate", "Next expiry"],
  },
  vendors: {
    key: "vendors",
    title: "Vendor register",
    sub: "Supply partners, GEM contracts, and credit terms",
    cols: ["Vendor", "Category", "GEM contract", "Credit terms", "Status"],
  },
  departments: {
    key: "departments",
    title: "Hospital departments",
    sub: "Issue points connected to the central store",
    cols: ["Department", "Location", "Head", "Contact"],
  },
  indents: {
    key: "indents",
    title: "Department indents",
    sub: "From requisition to issue and delivery sign-off",
    cols: ["Indent number", "Department", "Raised by", "Items", "Status", "Actions"],
  },
  receiving: {
    key: "receiving",
    title: "Purchase receiving",
    sub: "GRNs, batches, expiry, and store intake",
    cols: ["GRN / Invoice", "Vendor", "Items", "Total", "Payment", "Actions"],
  },
};

function nextExpiry(item) {
  const batches = (item.batches || []).filter((b) => b.expiry && (b.qty || 0) > 0);
  if (batches.length === 0) return item.expiry || "—";
  return batches.map((b) => b.expiry).sort()[0];
}

export default function ListView({ page, rows, onOpenIndent, onOpenVendor, company }) {
  const c = CONFIGS[page];
  const [expanded, setExpanded] = useState(null);
  if (!c) return null;
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <section className="panel table-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">{page.toUpperCase()}</p>
          <h3>{c.title}</h3>
          <p className="muted">{c.sub}</p>
        </div>
        <span className="record-count" data-testid={`${page}-record-count`}>{safeRows.length} records</span>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr>{c.cols.map((x) => <th key={x}>{x}</th>)}</tr></thead>
          <tbody>
            {safeRows.map((x, i) => (
              <Fragment key={x.id || i}>
                <tr data-testid={`${page}-row-${i}`}>
                  {page === "inventory" && (
                    <>
                      <td><strong>{x.name}</strong><small>{x.sku}</small></td>
                      <td>{x.category}</td>
                      <td>{x.vendor}</td>
                      <td>
                        <strong>{Number(x.stock || 0).toLocaleString()}</strong> {x.base_unit}
                        <button className="text-btn" data-testid={`toggle-batches-${i}`} onClick={() => setExpanded(expanded === i ? null : i)}>
                          <Layers size={12} /> {(x.batches || []).length} batches
                        </button>
                      </td>
                      <td>{money(x.rate)}</td>
                      <td>{nextExpiry(x)}</td>
                    </>
                  )}
                  {page === "vendors" && (
                    <>
                      <td>
                        <strong>{x.name}</strong>
                        <small>{x.phone}</small>
                        <button className="text-btn" data-testid={`open-ledger-${i}`} onClick={() => onOpenVendor?.(x)}>View ledger →</button>
                      </td>
                      <td>{x.category}</td>
                      <td className="mono">{x.gem_contract || "—"}</td>
                      <td>{x.credit_terms}</td>
                      <td><StatusBadge value={x.status} /></td>
                    </>
                  )}
                  {page === "departments" && (
                    <>
                      <td><strong>{x.name}</strong></td>
                      <td>{x.floor || "—"}</td>
                      <td>{x.head || "—"}</td>
                      <td>{x.contact || "—"}</td>
                    </>
                  )}
                  {page === "indents" && (
                    <>
                      <td className="mono">
                        <strong>{x.id}</strong>
                        <small>{x.upload_name && <><FileText size={13} /> {x.upload_name}</>}</small>
                      </td>
                      <td>{x.department}</td>
                      <td>{x.raised_by}<small>{x.raised_by_role}</small></td>
                      <td>{x.items?.length || 0} lines</td>
                      <td><StatusBadge value={x.status} /></td>
                      <td>
                        <div className="row-actions">
                          {x.status !== "Delivered" && (
                            <button className="text-btn" data-testid={`issue-indent-${i}`} onClick={() => onOpenIndent?.(x)}>
                              <PackageCheck size={13} /> Issue
                            </button>
                          )}
                          <button className="text-btn" data-testid={`print-indent-${i}`} onClick={() => printIndent(x, company)}>
                            <Printer size={13} /> Print PDF
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                  {page === "receiving" && (
                    <>
                      <td className="mono"><strong>{x.invoice_number || x.id}</strong><small>{x.id}</small></td>
                      <td>{x.vendor}</td>
                      <td>{x.items?.length || 0} lines<small>{x.items?.map((li) => li.item).join(", ")}</small></td>
                      <td><strong>{money(x.total)}</strong><small>Paid {money(x.amount_paid)}</small></td>
                      <td><StatusBadge value={x.payment_status} /></td>
                      <td>
                        <button className="text-btn" data-testid={`print-receipt-${i}`} onClick={() => printReceipt(x, company)}>
                          <Printer size={13} /> Print GRN
                        </button>
                      </td>
                    </>
                  )}
                </tr>
                {page === "inventory" && expanded === i && (
                  <tr className="batch-expand" data-testid={`batches-row-${i}`}>
                    <td colSpan={6}>
                      <div className="batches-panel">
                        <div className="batches-head">
                          <strong>Batches (FIFO — oldest expiry issued first)</strong>
                          <small>Total {Number(x.stock || 0).toLocaleString()} {x.base_unit}</small>
                        </div>
                        {(x.batches || []).length === 0 && <p className="muted">No batches recorded yet.</p>}
                        <table className="batches-table">
                          <thead><tr><th>Batch #</th><th>Qty on hand</th><th>Expiry</th><th>Received</th><th>Rate</th></tr></thead>
                          <tbody>
                            {(x.batches || []).slice().sort((a, b) => (a.expiry || "9999") > (b.expiry || "9999") ? 1 : -1).map((b, bi) => (
                              <tr key={bi}>
                                <td className="mono">{b.batch}</td>
                                <td>{Number(b.qty || 0).toLocaleString()} {x.base_unit}</td>
                                <td>{b.expiry || "—"}</td>
                                <td>{b.received_date || "—"}</td>
                                <td>{money(b.rate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {safeRows.length === 0 && <div className="empty">No records to display.</div>}
      </div>
    </section>
  );
}
